// Ficha 09 — Entrega, variante CONTADO: las tareas compartidas y el cierre de la venta. Confirmar las
// piezas las fija con /pieces y luego cierra; sin piezas rastreadas cierra al entrar. En la apertura
// de cuenta solo quedan las tareas: no hay venta que cerrar.
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Check } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { useWizardSession } from '../api/useWizard';
import { accountOrigin, alreadyHadAccount } from '../accountWait';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';
import { StateIcon } from '../components/KycScreenBits';
import { DeliveryTasks } from '../components/delivery/DeliveryTasks';
import { SaleStatus } from '../components/delivery/SaleStatus';
import { useDeliveryTasks } from '../components/delivery/useDeliveryTasks';
import { PieceIdentifiers } from '../components/delivery/PieceIdentifiers';
import { useSaleClosing } from '../components/delivery/useSaleClosing';
import {
    ACCOUNT_OPENING_DELIVERY_EYEBROW,
    ACCOUNT_OPENING_DELIVERY_SUB,
    ACCOUNT_OPENING_DELIVERY_TITLE,
    hasAccountOpeningMark,
} from '../accountOpening';

const EYEBROW = 'Entrega';

export function DeliveryStep({ sessionId }: { sessionId: string }) {
    const { data: session } = useWizardSession(sessionId);
    if (session && hasAccountOpeningMark(session)) {
        return <AccountOpeningDelivery sessionId={sessionId} />;
    }
    return <SaleDelivery sessionId={sessionId} />;
}

/** Apertura: app, registro y tarjeta. El gate son solo las tareas; nunca toca el cierre de venta. */
function AccountOpeningDelivery({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    const tasks = useDeliveryTasks(sessionId, session, !!session);
    if (!session) return <Loading size="lg" />;

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow={ACCOUNT_OPENING_DELIVERY_EYEBROW}
                title={ACCOUNT_OPENING_DELIVERY_TITLE}
                sub={ACCOUNT_OPENING_DELIVERY_SUB}
            />
            <WizardCardBody className="flex flex-col gap-3">
                <DeliveryTasks tasks={tasks} accountOpening />
            </WizardCardBody>
            <WizardFootNav>
                <Button
                    disabled={!tasks.settled}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({
                            to: '/wizard/s/$sessionId/$step',
                            params: { sessionId, step: 'referrals' },
                        })
                    }
                >
                    Continuar a Referidos
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}

function SaleDelivery({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const closing = useSaleClosing(sessionId, session, refresh, {
        withFinancedPhone: false,
        canClose: true,
        pinOnConfirm: true,
    });
    // El comprador que YA TRAÍA cuenta no recibe app ni tarjeta: para él este paso solo cierra la
    // venta y sigue. Es `alreadyHadAccount`, NO `accountAlreadyOpen` — ver su nota en accountWait.
    const skipsDelivery = !!session && alreadyHadAccount(accountOrigin(session));
    const tasks = useDeliveryTasks(sessionId, session, !!session && !skipsDelivery);

    // El cierre (idempotente por UNIQ#FOLIO) vive en `useSaleClosing`, con el error visible y retry.
    const { saleRef } = closing;
    const saleClosed = !!saleRef;

    // Brincar el paso es seguir DESPUÉS del cierre, nunca en su lugar: sin la venta registrada no
    // quedaría rastro de ella.
    useEffect(() => {
        if (!skipsDelivery || !saleClosed) return;
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'referrals' },
        });
    }, [skipsDelivery, saleClosed, navigate, sessionId]);

    if (!session) return <Loading size="lg" />;
    const title = 'Entrega del equipo';
    // Cómo va el cierre de la venta. Vive en el CUERPO de la card: el pie es solo navegación.
    const saleStatus = (
        <SaleStatus
            saleRef={saleRef}
            error={closing.statusError}
            retrying={closing.closing || closing.pinning}
            onRetry={closing.retry}
            pendingLabel={
                closing.piecesConfirmed
                    ? 'Registrando la venta…'
                    : 'La venta se registra al confirmar todas las piezas.'
            }
        />
    );
    // Las piezas serializadas se identifican antes del cierre; ya cerrada no hay nada que capturar.
    const pieces =
        closing.pieces.length > 0 && !saleClosed ? (
            <section className="flex flex-col gap-2 rounded-[10px] border-[1.5px] border-primary bg-accent/50 px-4 py-3.5">
                <div>
                    <div className="text-[13.5px] font-semibold">Identificar las piezas</div>
                    <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Escanea o teclea el IMEI o la serie de cada pieza. Los productos por
                        cantidad no piden nada.
                    </div>
                </div>
                <PieceIdentifiers
                    sessionId={sessionId}
                    closing={closing}
                    disabled={closing.closing || closing.pinning}
                />
            </section>
        ) : null;

    // ── El cliente ya traía cuenta: nada que entregar ─────────────────────────
    // Pantalla de paso, no de espera: el efecto de arriba navega a Referidos en cuanto la venta
    // queda registrada. Si el cierre falla se queda aquí, con el motivo y el Reintentar a la vista.
    if (skipsDelivery) {
        return (
            <WizardCard>
                <WizardCardHead
                    eyebrow={EYEBROW}
                    title={title}
                    sub="Este cliente no necesita nada de este paso."
                />
                <WizardCardBody>
                    <div className="text-center">
                        <StateIcon tone="success">
                            <Check className="size-7" aria-hidden />
                        </StateIcon>
                        <p className="mb-1 text-[15px] font-semibold">
                            El cliente ya tiene su cuenta y su tarjeta
                        </p>
                        <p className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            Ya tiene app y tarjeta. Entrégale el equipo.
                        </p>
                    </div>
                    {pieces && <div className="mt-4 text-left">{pieces}</div>}
                    <div className="mt-4 text-center">{saleStatus}</div>
                </WizardCardBody>
                <WizardFootNav />
            </WizardCard>
        );
    }

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow={EYEBROW}
                title={title}
                sub="Piezas, app, registro y tarjeta."
            />

            <WizardCardBody className="flex flex-col gap-3">
                {pieces}
                <DeliveryTasks tasks={tasks} />
                {saleStatus}
            </WizardCardBody>

            <WizardFootNav>
                {/* El gate son las tareas Y el cierre: en Bienvenida se acredita la recarga, y
                    acreditarla sobre una venta sin registrar no tiene vuelta atrás. */}
                <Button
                    disabled={!tasks.settled || !saleClosed}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({
                            to: '/wizard/s/$sessionId/$step',
                            params: { sessionId, step: 'referrals' },
                        })
                    }
                >
                    Continuar a Referidos
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}
